import {
  TSelectFilterGroupInfo,
  TFilterGroupModel,
  TSearchFilterGroupInfo,
  TFilterGroupInfo,
  TDateGroupInfo,
  TOrgFilter,
  TDurationGroupInfo,
  TTargetDateGroupInfo,
  TSingleSelectFilterGroupInfo,
  TMultiSelectFilterGroupInfo,
} from './ui-filter.types';
import { UiFilterPipeline } from './ui-filter.pipeline';
import moment from 'moment';
import _ from 'lodash';
import { getLeafNodeCount } from '@monorepo/ui';

export const UiFilterModifiers = {
  createFilterModelList: <T>(
    input: T[],
    createModel: (elm: T) => TFilterGroupInfo,
    dateFilter?: TDateGroupInfo,
  ): TFilterGroupModel<T> =>
    UiFilterPipeline.computeFilterModel({
      groups: dateFilter ? [dateFilter, ...input.map(createModel)] : input.map(createModel),
      hasSelectedFilters: false,
      originalData: input,
      selectedFilters: [],
      isValid: false,
    }),

  updateFilterModelList: <T>(
    updatedModelList: TFilterGroupModel<T>,
    prevModelList: TFilterGroupModel<T>,
  ): TFilterGroupModel<T> =>
    UiFilterPipeline.computeFilterModel({
      ...updatedModelList,
      groups: updatedModelList.groups.map((groupItem) => {
        switch (groupItem.type) {
          case 'ORG_HIERARCHY_FILTER': {
            const item = prevModelList.groups.find((v) => v.type === 'ORG_HIERARCHY_FILTER') as TOrgFilter | undefined;
            const orgHierarchySelectedTree = groupItem.dynamicOrgHierarchy?.dynamicTree;
            if (!item) return groupItem;
            if (groupItem.dynamicOrgHierarchy) {
              if (orgHierarchySelectedTree) {
                return {
                  ...groupItem,
                  filteredOrgTree: orgHierarchySelectedTree,
                  leafNodesSelected: getLeafNodeCount(orgHierarchySelectedTree),
                };
              }
              return {
                ...groupItem,
                filteredOrgTree: { ...groupItem.filteredOrgTree },
              };
            }
            return { ...item, filteredOrgTree: { ...item.filteredOrgTree } };
          }
          case 'DATE_FILTER': {
            const filterItem = prevModelList.groups
              .filter((v) => v.type === 'DATE_FILTER')
              .find((pv) => pv.key === groupItem.key) as TDateGroupInfo | undefined;
            const prevSelectedValues = filterItem
              ? new Set(filterItem.items.filter((v) => v.isSelected).map((v) => v.value))
              : undefined;
            return {
              ...groupItem,
              items: groupItem.items.map((item) => ({
                ...item,
                isSelected: prevSelectedValues ? prevSelectedValues.has(item.value) : false,
              })),
            };
          }
          case 'DURATION_FILTER': {
            const filterItem = prevModelList.groups
              .filter((v) => v.type === 'DURATION_FILTER')
              .find((pv) => pv.key === groupItem.key) as TDurationGroupInfo | undefined;
            const prevSelectedTypes = filterItem
              ? new Set(filterItem.items.filter((v) => v.isSelected).map((v) => v.value.type))
              : undefined;
            return {
              ...groupItem,
              items: groupItem.items.map((item) => {
                const isSelected = prevSelectedTypes ? prevSelectedTypes.has(item.value.type) : false;
                if (isSelected) {
                  return {
                    ...item,
                    value: {
                      ...item.value,
                      displayString: formatCustomDurationString(item.value.start, item.value.end),
                    },
                    isSelected,
                  };
                } else
                  return {
                    ...item,
                    isSelected,
                  };
              }),
            };
          }
          /**
           * @deprecated
           * Note: Below will be deprected and deleted since TMultiSelectFilterGroupInfo supports localization and is more generic
           */
          case 'SELECT_FILTER': {
            const filterItem = prevModelList.groups
              .filter((v) => v.type === 'SELECT_FILTER')
              .find((pv) => pv.key === groupItem.key) as TSelectFilterGroupInfo | undefined;
            const prevSelectedValues = filterItem
              ? new Set(filterItem.items.filter((v) => v.isSelected).map((v) => v.value))
              : undefined;
            return {
              ...groupItem,
              items: groupItem.items.map((item) => ({
                ...item,
                isSelected: prevSelectedValues ? prevSelectedValues.has(item.value) : false,
              })),
            };
          }
          case 'MULTI_SELECT_FILTER': {
            const filterItem = prevModelList.groups
              .filter((v) => v.type === 'MULTI_SELECT_FILTER')
              .find((pv) => pv.key === groupItem.key) as TMultiSelectFilterGroupInfo | undefined;
            const prevSelectedValues = filterItem
              ? new Set(filterItem.items.filter((v) => v.isSelected).map((v) => v.value))
              : undefined;
            return {
              ...groupItem,
              items: groupItem.items.map((item) => ({
                ...item,
                isSelected: prevSelectedValues ? prevSelectedValues.has(item.value) : false,
              })),
            };
          }
          case 'SINGLE_SELECT_FILTER': {
            const prevSingleSelectGroup = prevModelList.groups
              .filter((v) => v.type === 'SINGLE_SELECT_FILTER')
              .find((pv) => pv.key === groupItem.key) as TSingleSelectFilterGroupInfo;
            return {
              ...groupItem,
              selectedValue: prevSingleSelectGroup.selectedValue,
            };
          }
          case 'SEARCH_FILTER': {
            const filterItem = prevModelList.groups
              .filter((v) => v.type === 'SEARCH_FILTER')
              .find((pv) => pv.key === groupItem.key) as TSearchFilterGroupInfo;
            return {
              ...groupItem,
              text: filterItem.text,
            };
          }

          case 'TARGET_DATE_FILTER': {
            const selectedValues =
              (prevModelList?.groups.find((x) => x.type === 'TARGET_DATE_FILTER') as TTargetDateGroupInfo)
                ?.selectedValues || [];
            return {
              ...groupItem,
              selectedValues,
            };
          }
        }
      }),
    }),

  removeAllFilterValue: <T>(filterGroupModel: TFilterGroupModel<T>): TFilterGroupModel<T> => {
    const filterGroup = filterGroupModel.groups.map((group) => {
      switch (group.type) {
        case 'ORG_HIERARCHY_FILTER': {
          return {
            ...group,
            filteredOrgTree: {
              ...group.filteredOrgTree,
              values: [],
            },
            leafNodesSelected: 0,
          };
        }
        case 'TARGET_DATE_FILTER': {
          return {
            ...group,
            selectedValues: [],
          };
        }
        case 'SELECT_FILTER':
          return {
            ...group,
            items: group.items.map((item) => ({
              ...item,
              isSelected: false,
            })),
          };
        case 'MULTI_SELECT_FILTER':
          return {
            ...group,
            items: group.items.map((item) => ({
              ...item,
              isSelected: false,
            })),
          };
        case 'SINGLE_SELECT_FILTER':
          return {
            ...group,
            selectedValue: undefined,
          };
        case 'SEARCH_FILTER':
          return { ...group, text: '' };
        case 'DATE_FILTER':
          return {
            ...group,
            items: group.items.map((item) => ({
              ...item,
              isSelected: false,
            })),
          };
        case 'DURATION_FILTER':
          return {
            ...group,
            items: group.items.map((item) => ({
              ...item,
              isSelected: false,
            })),
          };
      }
    });

    return UiFilterPipeline.computeFilterModel({
      ...filterGroupModel,
      groups: filterGroup,
    });
  },

  sanitaize: <T>(filterGroupModel: TFilterGroupModel<T>): TFilterGroupModel<T> => {
    return UiFilterPipeline.computeFilterModel(filterGroupModel);
  },
  removeFilterValue: <T>(
    filterGroupModel: TFilterGroupModel<T>,
    groupModeInfo: TFilterGroupInfo,
    removeVaue: string | number,
  ): TFilterGroupModel<T> =>
    UiFilterPipeline.computeFilterModel({
      ...filterGroupModel,
      groups: removeFilterValue(filterGroupModel, groupModeInfo.key, removeVaue),
    }),

  filterUpdateByGroup: <T>(
    filterGroupModel: TFilterGroupModel<T>,
    groupModeInfo: TFilterGroupModel<T>['groups'][number],
  ): TFilterGroupModel<T> =>
    UiFilterPipeline.computeFilterModel({
      ...filterGroupModel,
      groups: filterGroupModel.groups.map((v) => {
        return v.key === groupModeInfo.key ? { ...groupModeInfo } : v;
      }),
    }),

  sortFilterItems: <T>(filterGroupModel: TFilterGroupModel<T>): TFilterGroupModel<T> =>
    UiFilterPipeline.computeFilterModel({
      ...filterGroupModel,
      groups: filterGroupModel.groups.map((group) => {
        switch (group.type) {
          case 'MULTI_SELECT_FILTER':
            return {
              ...group,
              items: _.sortBy(group.items, formatItemForSort),
            };
          case 'SELECT_FILTER':
            return {
              ...group,
              items: _.sortBy(group.items, formatItemForSort),
            };
          case 'SINGLE_SELECT_FILTER':
            return {
              ...group,
              items: _.sortBy(group.items, formatItemForSort),
            };
          default:
            return group;
        }
      }),
    }),
};

const removeFilterValue = <T>(
  filterGroupModel: TFilterGroupModel<T>,
  key: string | number,
  removeFilterValue: string | number,
) => {
  return filterGroupModel.groups.map((group) => {
    switch (group.type) {
      case 'ORG_HIERARCHY_FILTER':
        return {
          ...group,
          filteredOrgTree: { ...group.filteredOrgTree, values: group.key === key ? [] : group.filteredOrgTree.values },
        };
      case 'TARGET_DATE_FILTER':
        return {
          ...group,
          selectedValues: group.key === key ? [] : group.selectedValues,
        };
      case 'SELECT_FILTER':
        return {
          ...group,
          items: group.items.map((item) => ({
            ...item,
            isSelected: group.key === key && item.value === removeFilterValue ? false : item.isSelected,
          })),
        };
      case 'MULTI_SELECT_FILTER':
        return {
          ...group,
          items: group.items.map((item) => ({
            ...item,
            isSelected: group.key === key && item.value === removeFilterValue ? false : item.isSelected,
          })),
        };
      case 'SINGLE_SELECT_FILTER':
        return {
          ...group,
          selectedValue: group.key === key ? undefined : group.selectedValue,
        };
      case 'DATE_FILTER':
        return {
          ...group,
          items: group.items.map((item) => ({
            ...item,
            isSelected: group.key === key ? false : item.isSelected,
          })),
        };
      case 'DURATION_FILTER':
        return {
          ...group,
          items: group.items.map((item) => ({
            ...item,
            isSelected: group.key === key ? false : item.isSelected,
          })),
        };
      case 'SEARCH_FILTER':
        return { ...group, text: '' };
    }
  });
};

function formatItemForSort(
  item:
    | TMultiSelectFilterGroupInfo['items'][number]
    | TSingleSelectFilterGroupInfo['items'][number]
    | TSelectFilterGroupInfo['items'][number],
): string | number {
  if ('displayName' in item) {
    return _.isNumber(item.displayName) ? item.displayName : item.displayName.toLowerCase().replace(/^\s+/, '');
  } else {
    return _.isNumber(item.value) ? item.value : item.value.toLowerCase().replace(/^\s+/, '');
  }
}

function formatCustomDurationString(start: string | number | undefined, end: string | number | undefined) {
  const startDate = moment(start).format('DD/MM/YYYY');
  const endDate = moment(end).format('DD/MM/YYYY');
  return startDate + ' - ' + endDate;
}
